import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, BookOpen, DollarSign, FileText, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

function Stat({ label, value, sub, testId }) {
    return (
        <div className="st-surface p-5" data-testid={testId}>
            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>
                {label}
            </div>
            <div className="font-serif text-3xl mt-2" style={{ color: "var(--st-navy)" }}>
                {value}
            </div>
            {sub && <div className="text-xs mt-1" style={{ color: "var(--st-ink-muted)" }}>{sub}</div>}
        </div>
    );
}

const ACTION_LABELS = {
    "student.force_refund": "Force refund",
    "subscription.force_refund": "Force refund",
    "subscription.cancel_at_period_end": "Cancel at period end",
    "pricing.update": "Pricing updated",
    "blog.create": "Blog post created",
    "blog.update": "Blog post updated",
    "blog.delete": "Blog post deleted",
    "site_settings.update": "Site settings updated",
    "phase.unlock": "Phase unlocked",
    "phase.lock": "Phase locked",
    "lesson.update": "Lesson updated",
    "lesson.delete": "Lesson deleted",
    "module.update": "Module updated",
};

export default function AdminOverview() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.adminOverview().then(setData).catch((e) => setErr(e.message));
    }, []);

    if (err) {
        return <div className="p-8 text-sm" style={{ color: "#B42318" }} data-testid="admin-overview-error">{err}</div>;
    }
    if (!data) return <div className="p-8 text-sm" data-testid="admin-overview-loading">Loading…</div>;

    const { students, content, journal, recent_audit } = data;

    return (
        <section className="max-w-6xl mx-auto px-6 py-10 space-y-8" data-testid="admin-overview-page">
            <div>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                    Admin Overview
                </div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>
                    Platform at a glance
                </h1>
                <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                    Real-time enrollment, curriculum, and content health.
                </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Total students" value={students.total} sub={`${students.verified} verified`} testId="stat-students-total" />
                <Stat label="Active subs" value={students.active_subscriptions} sub={`${students.monthly} monthly · ${students.yearly} yearly`} testId="stat-active-subs" />
                <Stat label="Past due" value={students.past_due} sub="in grace period" testId="stat-past-due" />
                <Stat label="Canceled" value={students.canceled} sub="all-time" testId="stat-canceled" />
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Phases" value={content.phases} testId="stat-phases" />
                <Stat label="Modules" value={content.modules} sub={`${content.lessons} lessons`} testId="stat-modules" />
                <Stat label="Quizzes" value={content.quizzes} testId="stat-quizzes" />
                <Stat label="Journal entries" value={journal.total_entries} sub={`${journal.open} open`} testId="stat-journal" />
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="st-surface p-6 lg:col-span-2" data-testid="admin-recent-audit">
                    <div className="flex items-center justify-between">
                        <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                            Recent admin actions
                        </div>
                        <Link to="/admin/audit-log" className="link-royal text-xs">View full log →</Link>
                    </div>
                    {recent_audit.length === 0 ? (
                        <p className="mt-4 text-sm italic" style={{ color: "var(--st-silver-500)" }}>No admin actions recorded yet.</p>
                    ) : (
                        <ul className="mt-4 space-y-2">
                            {recent_audit.map((r, i) => (
                                <li key={i} className="flex items-start justify-between gap-4 py-2" style={{ borderBottom: "1px solid var(--st-line)" }} data-testid={`audit-row-${i}`}>
                                    <div className="text-sm">
                                        <div style={{ color: "var(--st-navy)" }} className="font-semibold">
                                            {ACTION_LABELS[r.action] || r.action}
                                        </div>
                                        <div className="text-xs mt-0.5" style={{ color: "var(--st-silver-500)" }}>
                                            {r.target_user_id ? `Target user ${r.target_user_id.slice(0, 8)}…` : "System"}
                                        </div>
                                    </div>
                                    <div className="text-xs whitespace-nowrap" style={{ color: "var(--st-silver-500)" }}>
                                        {new Date(r.timestamp).toLocaleString()}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="st-surface p-6" data-testid="admin-quick-links">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                        Quick actions
                    </div>
                    <ul className="mt-4 space-y-2">
                        {[
                            { to: "/admin/students", icon: Users, label: "Manage students" },
                            { to: "/admin/curriculum", icon: BookOpen, label: "Edit curriculum" },
                            { to: "/admin/pricing", icon: DollarSign, label: "Update pricing" },
                            { to: "/admin/blog", icon: FileText, label: "Write blog post" },
                            { to: "/admin/journal", icon: TrendingUp, label: "Review journals" },
                        ].map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm hover:bg-[var(--st-silver-50)]"
                                    style={{ color: "var(--st-navy)" }}
                                    data-testid={`quick-${l.to.split("/").pop()}`}
                                >
                                    <l.icon size={14} strokeWidth={1.6} />
                                    <span>{l.label}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}
